import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { DrawerLayoutWrapper, MDialog } from 'common';
import { DrawerContext } from 'common/drawer/DrawerContext';
import {
  Divider,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  useTheme,
} from '@mui/material';
import * as R from 'ramda';
import DataDisplayItem from 'feature/shipping/batches/components/DataDisplayItem';
import { Info } from '@mui/icons-material';
import { removeLeadingZeros } from 'utils/helpers/format';
import { useGetProductBalancesQuery } from '../reconsignmentsEndpoints';

// TODO: Move this to global format utility once we review and update all formatNumberWithCommas usages
// to handle zero values consistently across the app
// Local function to handle zero values properly for product balances
const formatBalanceWithCommas = value => {
  if (value == null || value === '') return '';
  const numString = value.toString();
  return numString.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const infoContainerStyle = {
  // TODO: remove excessive padding on y-axis for all creation dialogs
  '&.MuiDialogContent-root': {
    py: 0,
  },
  display: 'grid',
  gap: { lg: 2, xs: 0 },
  gridTemplateColumns: { lg: '1fr' },
  justifyContent: 'center',
  p: 2,
};

const ConfigurationStepVolume = ({
  next,
  prev,
  reconsignmentState,
  setReconsignmentState,
  setOpenDialog,
  openDialog,
}) => {
  const { setDrawerComponentProps } = useContext(DrawerContext);
  const { fromLocation, inventoryOwner, origin, product, rvp, toLocation, volume, fullFilment } =
    reconsignmentState;
  const [validationState, setValidationState] = useState({
    volume: '',
  });
  const selectedOriginIds = origin.map(R.prop('originId'));
  const selectedAffiliate = inventoryOwner;
  const [selectedFulfillmentOption, setSelectedFulfillmentOption] = useState(null);

  const handleDrawerClose = () => {
    setOpenDialog(false);
    setDrawerComponentProps({
      Component: '',
      forceCloseSelector: () => false,
      holdOpenSelector: () => false,
      refreshNotifications: false,
      resetWizard: undefined,
      setCurrentDialogOpen: () => {},
      type: '',
    });
  };

  const handleDrawerCloseConfirm = () => {
    setOpenDialog(true);
  };

  const { palette } = useTheme();

  const params = {
    inventoryOwner: {
      companyCustomerIdName: selectedAffiliate?.customerIdName,
      dataCompanyId: selectedAffiliate?.dataCompanyId,
    },
    locationId: fromLocation?.locationId,
    originIds: selectedOriginIds,
    productId: product?.productId,
    rvp: rvp !== null && rvp === 'Any' ? null : rvp,
  };

  const { data: productBalances = [] } = useGetProductBalancesQuery(
    { params },
    { skip: R.isNil(inventoryOwner) }
  );

  useEffect(() => {
    if (productBalances.length === 0 && selectedFulfillmentOption !== 'Any Inventory') {
      const defaultEmptyOptions = [{ label: null, volume: 0 }];
      setSelectedFulfillmentOption(null);
      setReconsignmentState({ ...reconsignmentState, fullFilment: defaultEmptyOptions });
    } else if (productBalances.anyInventory >= 0 && selectedFulfillmentOption === null) {
      if (fullFilment && fullFilment[0] && fullFilment[0].label !== null) {
        setSelectedFulfillmentOption(fullFilment[0].label);
      } else {
        const defaultOptions = [
          { label: 'Any Inventory', volume: productBalances.anyInventory || 0 },
        ];
        setReconsignmentState({ ...reconsignmentState, fullFilment: defaultOptions });
        setSelectedFulfillmentOption('Any Inventory');
      }
    }
  }, [
    fullFilment,
    productBalances,
    reconsignmentState,
    selectedFulfillmentOption,
    setReconsignmentState,
  ]);

  useEffect(() => {
    if (volume && volume !== null && fullFilment && fullFilment[0]) {
      const sanitizedValue = volume.replace(/,/g, '').trim();
      const isNumeric = /^[0-9]+$/.test(sanitizedValue);
      if (!isNumeric) {
        setValidationState({ ...validationState, volume: 'Volume must be an integer' });
      } else {
        const numericValue = parseInt(volume.replace(/,/g, ''));
        if (Number.isNaN(numericValue)) {
          setValidationState({ ...validationState, volume: 'Volume must be a number' });
        } else if (numericValue === 0) {
          setValidationState({ ...validationState, volume: 'Volume must be greater than 0' });
        } else if (fullFilment[0].volume >= numericValue) {
          setValidationState({ ...validationState, volume: '' });
        } else if (fullFilment[0].volume < numericValue) {
          setValidationState({
            ...validationState,
            volume: 'Volume should not be more than the selected fulfillment',
          });
        }
      }
    }
    if (productBalances.length === 0) {
      setValidationState({ ...validationState, volume: '' });
      setReconsignmentState({ ...reconsignmentState, volume: '' });
    }
  }, [
    fullFilment,
    productBalances,
    reconsignmentState,
    setReconsignmentState,
    validationState,
    volume,
  ]);

  const isInvalid = [
    R.isNil(fullFilment),
    R.isEmpty(volume),
    R.isNil(volume),
    R.not(R.isEmpty(validationState.volume)),
  ].includes(true);

  const handleVolumeChange = ({ target: { value } }) => {
    const sanitizedValue = removeLeadingZeros(value.replace(/[^0-9]/g, ''));
    const formattedValue = sanitizedValue ? Number(sanitizedValue).toLocaleString('en-US') : '';

    if (!sanitizedValue && sanitizedValue !== 0) {
      setValidationState({ ...validationState, volume: 'Volume must be an integer' });
      setReconsignmentState({ ...reconsignmentState, volume: '' });
      return;
    }

    if (sanitizedValue === 0) {
      setValidationState({ ...validationState, volume: 'Volume must be greater than 0' });
    } else if (reconsignmentState.fullFilment[0].volume >= sanitizedValue) {
      setValidationState({ ...validationState, volume: '' });
      setReconsignmentState({ ...reconsignmentState, volume: formattedValue });
    } else {
      setValidationState({
        ...validationState,
        volume: 'Volume should not be more than the selected fulfillment',
      });
    }
    setReconsignmentState({
      ...reconsignmentState,
      volume: formattedValue,
    });
  };

  const createFulfillmentOptions = () => {
    const options = [
      { label: 'Any Inventory', volume: productBalances.anyInventory || 0 },
      { label: 'Any Available', volume: productBalances.anyAvailableInventory || 0 },
      { label: 'Any In-Transit', volume: productBalances.anyInTransitInventory || 0 },
      { label: 'Any Uncontrolled', volume: productBalances.anyUncontrolledInventory || 0 },
      {
        label: 'Uncontrolled Available',
        volume: productBalances.uncontrolledAvailableInventory || 0,
      },
      {
        label: 'Uncontrolled In-Transit',
        volume: productBalances.uncontrolledInTransitInventory || 0,
      },
      { label: 'Any Controlled', volume: productBalances.anyControlledInventory || 0 },
      {
        label: 'Controlled Available',
        volume: productBalances.controlledAvailableInventory || 0,
      },
      {
        label: 'Controlled In-Transit',
        volume: productBalances.controlledInTransitInventory || 0,
      },
    ];
    return options;
  };

  const handleFulfillmentOptionChange = value => {
    const fullFillArray = createFulfillmentOptions();
    const selectedValue = fullFillArray.find(R.propEq('label', value));
    setValidationState({ ...validationState, volume: '' });
    setReconsignmentState({ ...reconsignmentState, fullFilment: [selectedValue] });
    setSelectedFulfillmentOption(value);
    const sanitizedValue = volume.trim();
    const isNumeric = /^[0-9]+$/.test(sanitizedValue);
    if (!isNumeric) {
      setValidationState({ ...validationState, volume: 'Volume must be an integer' });
    } else {
      const numericValue = parseInt(volume.replace(/,/g, ''));
      if (Number.isNaN(numericValue)) {
        setValidationState({ ...validationState, volume: 'Volume must be an integer' });
      } else if (selectedValue.volume >= numericValue) {
        setValidationState({ ...validationState, volume: '' });
      } else if (selectedValue.volume < numericValue) {
        setValidationState({
          ...validationState,
          volume: 'Volume should not be more than the selected fulfillment',
        });
      }
    }
  };

  const isGasoline = R.equals(R.T(), R.prop('isGasoline', product));

  const actionButtons = (
    <Box sx={{ display: 'flex', justifyContent: 'space-around', width: 1 }}>
      <Button onClick={prev} sx={{ width: 150 }} variant="outlined">
        Back
      </Button>
      <Button disabled={isInvalid} onClick={next} sx={{ width: 150 }}>
        Continue
      </Button>
    </Box>
  );

  return (
    <DrawerLayoutWrapper
      buttons={actionButtons}
      headerButtonText="CLOSE"
      onHeaderButtonClick={handleDrawerCloseConfirm}
      title="New 1 to 1 Reconsignment"
    >
      <Box
        sx={{
          borderBottom: 1,
          borderBottomColor: 'graphic.light',
          p: 3,
          width: '100%',
        }}
      >
        <LinearProgress data-variant="magellanProgressBar" value={66} variant="determinate" />
        <Typography>Transfer Volume (2 of 3)</Typography>
      </Box>
      <Divider gap={32} />
      <Stack
        direction="row"
        display="flex"
        gap={32}
        justifyContent={{ sm: 'center', xs: 'space-between' }}
        sx={{
          display: 'grid',
          gridTemplateColumns: { lg: '1fr' },
          pr: 10,
        }}
      >
        <Typography variant="body1">
          <Box
            sx={{
              ...infoContainerStyle,
            }}
          >
            <DataDisplayItem label="From Location" value={R.prop('locationName', fromLocation)} />
            <DataDisplayItem label="To Location" value={R.prop('locationName', toLocation)} />
            <DataDisplayItem label="Product" value={R.prop('productName', product)} />
            {isGasoline && <DataDisplayItem label="RVP" value={rvp} />}
            {origin.length > 0 ? (
              <Box sx={{ display: 'block', whiteSpace: 'pre-line' }}>
                <DataDisplayItem
                  label="Origin"
                  value={origin.map(R.prop('originName')).join('\n')}
                />
              </Box>
            ) : (
              <DataDisplayItem label="Origin" value="Any" />
            )}
          </Box>
        </Typography>
      </Stack>
      <Divider gap={32} />
      <Box sx={infoContainerStyle}>
        <TextField
          disabled={productBalances.length === 0}
          error={!R.isEmpty(validationState.volume)}
          fullWidth
          helperText={validationState.volume}
          label="Transfer Volume"
          onChange={handleVolumeChange}
          slotProps={{
            input: {
              endAdornment: <InputAdornment position="end">bbl</InputAdornment>,
              inputProps: { inputMode: 'numeric', pattern: '[0-9]*' },
            },
          }}
          value={volume}
        />
        <Typography sx={{ pl: 3 }} variant="h7">
          How would you like to fulfill this order?
        </Typography>
        {productBalances.length === 0 && (
          <Box
            sx={{
              alignItems: { sm: 'center', xs: 'flex-start' },
              background: 'var(--states-success-100, #BBDEFB)',
              borderRadius: '4px',
              color: 'palette.info.light',
              direction: 'row',
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              p: 2,
            }}
          >
            <Info
              sx={{
                background: 'var(--states-success-100, #BBDEFB)',
                borderRadius: '4px',
                color: '#B7BCC5',
              }}
            />
            <Typography variant="body2">
              There 0 barrels matching your criteria at this location. Return to step 1 and adjust
              your selections.
            </Typography>
          </Box>
        )}
        <RadioGroup
          onChange={(_, value) => handleFulfillmentOptionChange(value)}
          value={selectedFulfillmentOption}
        >
          {createFulfillmentOptions().map(option => (
            <Box
              key={option.label}
              sx={{
                background:
                  selectedFulfillmentOption === option.label
                    ? palette.secondary.light
                    : palette.surface.white,
                border:
                  selectedFulfillmentOption === option.label
                    ? 'none'
                    : `1px solid ${palette.surface.swimlane}`,
                borderRadius: '21px',
                marginBottom: '12px',
                padding: '2px 16px',
                width: '100%',
              }}
            >
              <FormControlLabel
                control={
                  <Radio
                    sx={{
                      '&.Mui-checked': {
                        color: 'palette.success.main',
                      },
                    }}
                  />
                }
                disabled={option.volume <= 0}
                label={
                  <>
                    <Typography noWrap variant="body1">
                      {option.label}
                    </Typography>
                    <Typography noWrap variant="body1">{`${formatBalanceWithCommas(
                      option.volume
                    )} bbls`}</Typography>
                  </>
                }
                sx={{
                  '& .MuiFormControlLabel-label': {
                    alignItems: 'center',
                    display: 'flex',
                    flexWrap: 'nowrap',
                    gap: 2,
                    justifyContent: 'space-between',
                    width: '100%',
                  },
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'space-between',
                  margin: 0,
                  width: '100%',
                }}
                value={option.label}
              />
            </Box>
          ))}
        </RadioGroup>
      </Box>
      <MDialog
        header="Unsaved Changes"
        leftButtonAction={() => {
          setOpenDialog(false);
          handleDrawerClose();
        }}
        leftButtonText="Continue"
        open={openDialog}
        rightButtonAction={() => setOpenDialog(false)}
        rightButtonText="Keep Editing"
      >
        <Typography color="textPrimary" component="span" variant="subtitle1">
          Are you sure you want to leave this page? You have unsaved one to one reconsignment
          changes.
        </Typography>
      </MDialog>
    </DrawerLayoutWrapper>
  );
};

ConfigurationStepVolume.propTypes = {
  next: PropTypes.func.isRequired,
  openDialog: PropTypes.bool.isRequired,
  prev: PropTypes.func.isRequired,
  reconsignmentState: PropTypes.shape({
    fromLocation: PropTypes.shape({
      locationId: PropTypes.number,
    }),
    fullFilment: PropTypes.shape({}),
    inventoryOwner: PropTypes.shape({}),
    origin: PropTypes.arrayOf(),
    product: PropTypes.shape({
      productId: PropTypes.number,
    }),
    rvp: PropTypes.string,
    toLocation: PropTypes.shape({
      locationId: PropTypes.number,
    }),
    volume: PropTypes.string,
  }).isRequired,
  setOpenDialog: PropTypes.func.isRequired,
  setReconsignmentState: PropTypes.func.isRequired,
};

export default ConfigurationStepVolume;
